import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

// ─────────────────────────────────────────────────────────
//  Module-level: variant change detection infrastructure
// ─────────────────────────────────────────────────────────
//
// This bridge detects variant changes that originate OUTSIDE
// the native <variant-picker> — for example, from third-party
// swatch apps like Globo Color Swatch that mutate the hidden
// `input[name="id"]` and URL without firing the theme's
// native variant:update event.
//
// Detection layers (in order of arrival):
//
// 1. Document-level `change` event (real-world pattern A/C)
//    Catches any change event from any source and checks if
//    the hidden variant input's value has changed to a new,
//    un-notified variant.
//
// 2. History API monkey-patch
//    Catches history.pushState / replaceState calls (Globo
//    always updates the URL) and triggers the same check.
//
// 3. MutationObserver on input value attribute
//    Catches explicit attribute changes on the hidden input.
//
// Additionally:
// - A `resize` event is dispatched after the bridge completes
//   (real-world pattern C) to force gallery/slideshow layout
//   recalculation.
// - Native variant:update events are tracked in a module-level
//   Set to prevent double-processing (the native variant-picker
//   dispatches VariantUpdateEvent BEFORE its async URL update,
//   so by the time the URL changes the ID is already tracked).

// ─── History API monkey-patch ────────────────────────────

const { pushState: origPushState, replaceState: origReplaceState } = history;

history.pushState = function (...args) {
  const result = origPushState.apply(this, args);
  window.dispatchEvent(new CustomEvent('history:url-changed', { detail: { href: location.href } }));
  return result;
};

history.replaceState = function (...args) {
  const result = origReplaceState.apply(this, args);
  window.dispatchEvent(new CustomEvent('history:url-changed', { detail: { href: location.href } }));
  return result;
};

// ─── Native-variant tracking ─────────────────────────────
// A Set of variant IDs that were already handled via a
// native VariantUpdateEvent. Entries expire after 5 seconds.

const recentlyNotifiedVariants = new Set();
let cleanupTimer = null;

function markVariantNotified(variantId) {
  if (!variantId) return;
  recentlyNotifiedVariants.add(String(variantId));
  if (!cleanupTimer) {
    cleanupTimer = setTimeout(() => {
      recentlyNotifiedVariants.clear();
      cleanupTimer = null;
    }, 5000);
  }
}

/**
 * Normalizes a product option value or media alt group into a handle-like key.
 * @param {unknown} value
 * @returns {string}
 */
function normalizeMediaGroup(value) {
  if (value == null) return '';

  return String(value)
    .split('|')[0]
    .replace(/^\s*group\s*:/i, '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, 'and')
    .replace(/['"]/g, '')
    .replace(/[^\p{Letter}\p{Number}]+/gu, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Safely parses a JSON script.
 * @param {Element | null | undefined} script
 * @returns {any}
 */
function parseJsonScript(script) {
  if (!script?.textContent) return null;

  try {
    return JSON.parse(script.textContent);
  } catch (_error) {
    return null;
  }
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    // Layer 0: Track native variant:update events.
    // MUST be registered BEFORE #handleVariantUpdate so markVariantNotified()
    // fires first. Otherwise, #handleVariantUpdate would call replaceWith(),
    // which disconnects this element, aborts the controller, and removes
    // the Layer 0 listener before it ever fires — leaving the variant ID
    // un-tracked and causing the double-process guard to miss.
    target?.addEventListener(
      ThemeEvents.variantUpdate,
      (event) => {
        markVariantNotified(event.detail.resource?.id);
      },
      { signal }
    );

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // External variant bridge
    this.#setupExternalVariantBridge(signal);
    this.#setupGroupedMediaFiltering(signal);
    this.#scheduleGroupedMediaFiltering();
  }

  #controller = new AbortController();

  /** @type {AbortController | undefined} */
  #externalFetchAbortController;

  /** @type {string} */
  #lastProcessedExternalVariantId = '';

  /** @type {number | undefined} */
  #groupedMediaFrame;

  /** @type {number[]} */
  #groupedMediaTimeouts = [];

  /** @type {string} */
  #lastGroupedMediaSignature = '';

  /** @type {any[] | null | undefined} */
  #productVariantsCache;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#externalFetchAbortController?.abort();
    if (this.#groupedMediaFrame) cancelAnimationFrame(this.#groupedMediaFrame);
    this.#clearGroupedMediaTimeouts();
    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    const slide = this.slideshow?.refs?.slides?.[event.detail.index];
    const slideId = slide?.getAttribute('slide-id');

    if (slideId) {
      this.slideshow?.select({ id: slideId }, undefined, { animate: false });
    } else {
      this.slideshow?.select(event.detail.index, undefined, { animate: false });
    }
  };

  /**
   * Applies product media grouping after initial render, native variant changes,
   * and third-party swatch changes that mutate variant state asynchronously.
   *
   * @param {AbortSignal} signal
   */
  #setupGroupedMediaFiltering(signal) {
    const section = this.closest('.shopify-section');
    if (!section) return;

    section.addEventListener('click', this.#handlePotentialVariantClick, { signal, capture: true });
    this.addEventListener('click', this.#handleGroupedMediaZoomClick, { signal, capture: true });
    window.addEventListener('history:url-changed', this.#scheduleGroupedMediaFiltering, { signal });
    window.addEventListener('popstate', this.#scheduleGroupedMediaFiltering, { signal });
  }

  /** @param {Event} event */
  #handleGroupedMediaZoomClick = (event) => {
    if (!this.dataset.mediaGroupActive) return;
    if (!(event.target instanceof Element)) return;

    const slide = event.target.closest('slideshow-slide[data-media-id]');
    if (!(slide instanceof HTMLElement) || slide.hidden) return;
    if (!slide.classList.contains('product-media-container--zoomable')) return;
    if (slide.classList.contains('product-media-container--model')) return;

    const visibleSlides = Array.from(this.querySelectorAll('slideshow-slide[data-media-id]')).filter(
      (element) => element instanceof HTMLElement && !element.hidden
    );
    const index = visibleSlides.indexOf(slide);
    if (index < 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.refs.zoomDialogComponent?.open(index, event);
  };

  /** @param {Event} event */
  #handlePotentialVariantClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const gloTrigger = event.target.closest(
      [
        '[class*="glo"]',
        '[class*="Glo"]',
        '[class*="globo"]',
        '[class*="Globo"]',
        '[id*="glo"]',
        '[id*="Glo"]',
        '[id*="globo"]',
        '[id*="Globo"]',
      ].join(',')
    );

    if (gloTrigger) {
      this.#scheduleGroupedMediaFiltering();
    }
  };

  #scheduleGroupedMediaFiltering = () => {
    if (this.#controller.signal.aborted) return;
    if (this.#groupedMediaFrame) cancelAnimationFrame(this.#groupedMediaFrame);
    this.#clearGroupedMediaTimeouts();

    this.#groupedMediaFrame = requestAnimationFrame(() => {
      this.#groupedMediaFrame = undefined;
      this.#applyGroupedMediaFiltering();

      requestAnimationFrame(() => {
        if (!this.#controller.signal.aborted) {
          this.#selectFirstVisibleMedia();
        }
      });

      this.#groupedMediaTimeouts.push(
        window.setTimeout(() => {
          if (!this.#controller.signal.aborted) {
            this.#selectFirstVisibleMedia();
          }
        }, 90)
      );
    });
  };

  #clearGroupedMediaTimeouts() {
    for (const timeout of this.#groupedMediaTimeouts) {
      window.clearTimeout(timeout);
    }

    this.#groupedMediaTimeouts = [];
  }

  #applyGroupedMediaFiltering() {
    this.#captureOriginalMediaOrder();

    const selectedGroup = this.#getSelectedMediaGroup();
    let matchingMediaIds = this.#getVariantRangeMediaIds(selectedGroup);
    const mediaElements = this.#getMediaElements();
    const groupedMedia = mediaElements.filter((element) => this.#hasMediaGroupData(element));

    if (!selectedGroup) {
      this.#resetGroupedMediaFiltering();
      return;
    }

    if (!matchingMediaIds.size && groupedMedia.length) {
      matchingMediaIds = new Set(
        groupedMedia
          .filter((element) => this.#elementMatchesSelectedGroup(element, selectedGroup))
          .map((element) => element.dataset.mediaId)
          .filter(Boolean)
      );
    }

    if (!matchingMediaIds.size) {
      this.#resetGroupedMediaFiltering();
      return;
    }

    this.dataset.mediaGroupActive = selectedGroup;
    const galleryMediaIds = new Set(mediaElements.map((element) => element.dataset.mediaId).filter(Boolean));
    const variantGroupValues = this.#getVariantGroupValues();
    const packSpecificMediaIds = this.#getPackSpecificMediaIds(mediaElements);
    const groupedRunMediaIds = this.#getGroupedRunMediaIds(
      selectedGroup,
      variantGroupValues,
      this.#getOrderedMediaElements()
    );

    if (this.#shouldUseGroupedRunMediaIds(groupedRunMediaIds, packSpecificMediaIds)) {
      matchingMediaIds = groupedRunMediaIds;
    } else {
      matchingMediaIds = this.#addExplicitSelectedGroupMedia(
        matchingMediaIds,
        selectedGroup,
        variantGroupValues,
        mediaElements
      );
      const colorCorrectedMediaIds = this.#removeExplicitOtherGroupMedia(
        matchingMediaIds,
        selectedGroup,
        variantGroupValues,
        mediaElements
      );
      if (colorCorrectedMediaIds.size) {
        matchingMediaIds = colorCorrectedMediaIds;
      }
    }

    const packMediaFilter = this.#getSelectedPackMediaFilter(
      matchingMediaIds,
      galleryMediaIds,
      packSpecificMediaIds
    );
    const preferredMediaId = packMediaFilter.preferredMediaId;

    if (packMediaFilter.hasPackSelection) {
      const packFilteredMediaIds = new Set(matchingMediaIds);

      for (const mediaId of packMediaFilter.otherPackMediaIds) {
        packFilteredMediaIds.delete(mediaId);
      }

      if (preferredMediaId) {
        packFilteredMediaIds.add(preferredMediaId);
      }

      if (this.#shouldApplyPackFilteredMediaIds(matchingMediaIds, packFilteredMediaIds, packSpecificMediaIds)) {
        matchingMediaIds = packFilteredMediaIds;
      }
    }

    if (preferredMediaId) {
      matchingMediaIds.add(preferredMediaId);
    }

    const signature = this.#getGroupedMediaSignature(selectedGroup, preferredMediaId, matchingMediaIds);
    if (signature === this.#lastGroupedMediaSignature) {
      this.#selectFirstVisibleMedia();
      return;
    }

    this.#lastGroupedMediaSignature = signature;

    for (const element of this.#getFilterableElements()) {
      const mediaId = element.dataset.mediaId;
      this.#setGroupedMediaHidden(element, !mediaId || !matchingMediaIds.has(mediaId));
    }

    this.#reorderVisibleMedia(matchingMediaIds, preferredMediaId);
    this.#updateVisibleMediaCount(matchingMediaIds.size);
    this.#selectFirstVisibleMedia();
    window.dispatchEvent(new Event('resize'));
  }

  /**
   * Builds GLO-style media ranges from variant featured media markers:
   * selected group start -> next group start.
   *
   * @param {string} selectedGroup
   * @returns {Set<string>}
   */
  #getVariantRangeMediaIds(selectedGroup) {
    if (!selectedGroup) return new Set();

    const mediaElements = this.#getOrderedMediaElements();
    const variants = this.#getProductVariants();
    if (!mediaElements.length || !Array.isArray(variants)) return new Set();

    const mediaIds = mediaElements.map((element) => element.dataset.mediaId).filter(Boolean);
    const mediaIndexById = new Map(mediaIds.map((id, index) => [id, index]));
    const groupOptionIndex = Number.parseInt(this.dataset.groupOptionIndex || '0', 10) || 0;
    const groupStarts = new Map();

    for (const variant of variants) {
      const groupValue = Array.isArray(variant?.options)
        ? variant.options[groupOptionIndex] ?? variant.options[0]
        : variant?.[`option${groupOptionIndex + 1}`] ?? variant?.option1;
      const group = normalizeMediaGroup(groupValue);
      const mediaId = variant?.featured_media?.id ?? variant?.featured_image?.id;

      if (!group || !mediaId) continue;

      const mediaIdString = String(mediaId);
      if (!mediaIndexById.has(mediaIdString)) continue;

      if (!groupStarts.has(group)) {
        groupStarts.set(group, new Set());
      }
      groupStarts.get(group).add(mediaIdString);
    }

    if (!groupStarts.has(selectedGroup)) return new Set();

    const startIndex = this.#getFirstMediaIndexForGroup(groupStarts.get(selectedGroup), mediaIndexById);
    if (startIndex < 0) return new Set();

    const otherGroupStartIndexes = [];
    for (const [group, startIds] of groupStarts) {
      if (group === selectedGroup) continue;

      const index = this.#getFirstMediaIndexForGroup(startIds, mediaIndexById);
      if (index > startIndex) {
        otherGroupStartIndexes.push(index);
      }
    }

    const endIndex = otherGroupStartIndexes.length
      ? Math.min(...otherGroupStartIndexes)
      : mediaElements.length;

    return new Set(
      mediaElements
        .slice(startIndex, endIndex)
        .map((element) => element.dataset.mediaId)
        .filter(Boolean)
    );
  }

  /**
   * @param {Set<string>} startIds
   * @param {Map<string, number>} mediaIndexById
   * @returns {number}
   */
  #getFirstMediaIndexForGroup(startIds, mediaIndexById) {
    const indexes = Array.from(startIds)
      .map((id) => mediaIndexById.get(id))
      .filter((index) => typeof index === 'number');

    return indexes.length ? Math.min(...indexes) : -1;
  }

  #resetGroupedMediaFiltering() {
    delete this.dataset.mediaGroupActive;
    this.#lastGroupedMediaSignature = '';

    for (const element of this.#getFilterableElements()) {
      this.#setGroupedMediaHidden(element, false);
    }

    this.#restoreOriginalMediaOrder();
    this.#updateVisibleMediaCount();
    window.dispatchEvent(new Event('resize'));
  }

  /**
   * @param {string} selectedGroup
   * @param {string} preferredMediaId
   * @param {Set<string>} matchingMediaIds
   * @returns {string}
   */
  #getGroupedMediaSignature(selectedGroup, preferredMediaId, matchingMediaIds) {
    return [
      selectedGroup,
      preferredMediaId,
      Array.from(matchingMediaIds).sort().join(','),
    ].join('|');
  }

  /**
   * Avoid collapsing a color gallery to only one pack image if regular color
   * media exists. That fallback keeps the gallery accurate even when variant
   * featured media assignments are inconsistent.
   *
   * @param {Set<string>} colorMediaIds
   * @param {Set<string>} packFilteredMediaIds
   * @param {Set<string>} packSpecificMediaIds
   * @returns {boolean}
   */
  #shouldApplyPackFilteredMediaIds(colorMediaIds, packFilteredMediaIds, packSpecificMediaIds) {
    if (!packFilteredMediaIds.size) return false;

    const colorHasRegularMedia = Array.from(colorMediaIds).some((mediaId) => !packSpecificMediaIds.has(mediaId));
    const filteredHasRegularMedia = Array.from(packFilteredMediaIds).some(
      (mediaId) => !packSpecificMediaIds.has(mediaId)
    );

    return !colorHasRegularMedia || filteredHasRegularMedia;
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getMediaElements() {
    return Array.from(
      this.querySelectorAll(
        [
          'slideshow-slide[data-media-id]',
          '.media-gallery__grid > [data-media-id]',
          '.dialog-zoomed-gallery > [data-media-id]',
        ].join(',')
      )
    );
  }

  #captureOriginalMediaOrder() {
    for (const containerSelector of [
      'slideshow-slides',
      '.slideshow-controls__thumbnails',
      '.slideshow-controls__dots',
      '.dialog-thumbnails-list',
      '.dialog-zoomed-gallery',
      '.media-gallery__grid',
    ]) {
      const container = this.querySelector(containerSelector);
      if (!(container instanceof HTMLElement)) continue;

      Array.from(container.children)
        .filter((child) => child instanceof HTMLElement)
        .forEach((child, index) => {
          child.dataset.originalMediaOrder ||= String(index);
        });
    }
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getOrderedMediaElements() {
    const slideshowMedia = Array.from(this.querySelectorAll('slideshow-slide[data-media-id]'));
    if (slideshowMedia.length) return this.#sortByOriginalMediaOrder(slideshowMedia);

    return this.#sortByOriginalMediaOrder(Array.from(this.querySelectorAll('.media-gallery__grid > [data-media-id]')));
  }

  /**
   * @param {Element[]} elements
   * @returns {HTMLElement[]}
   */
  #sortByOriginalMediaOrder(elements) {
    return elements
      .filter((element) => element instanceof HTMLElement)
      .sort((a, b) => {
        const aOrder = Number.parseInt(a.dataset.originalMediaOrder || '', 10);
        const bOrder = Number.parseInt(b.dataset.originalMediaOrder || '', 10);

        if (Number.isNaN(aOrder) || Number.isNaN(bOrder)) return 0;

        return aOrder - bOrder;
      });
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getFilterableElements() {
    return Array.from(
      this.querySelectorAll(
        [
          'slideshow-slide[data-media-id]',
          '.media-gallery__grid > [data-media-id]',
          '.dialog-zoomed-gallery > [data-media-id]',
          '.dialog-thumbnails-list__thumbnail[data-media-id]',
          'slideshow-controls .slideshow-controls__thumbnail[data-media-id]',
          'slideshow-controls .slideshow-controls__dots [data-media-id]',
        ].join(',')
      )
    );
  }

  /**
   * @param {HTMLElement} element
   * @returns {string[]}
   */
  #getElementMediaGroups(element) {
    return [
      element.dataset.mediaGroup,
      normalizeMediaGroup(element.dataset.mediaGroupLabel),
    ].filter(Boolean);
  }

  /**
   * @returns {string[]}
   */
  #getVariantGroupValues() {
    const variants = this.#getProductVariants();
    if (!Array.isArray(variants)) return [];

    const groupOptionIndex = Number.parseInt(this.dataset.groupOptionIndex || '0', 10) || 0;
    const groupValues = new Set();

    for (const variant of variants) {
      const groupValue = Array.isArray(variant?.options)
        ? variant.options[groupOptionIndex] ?? variant.options[0]
        : variant?.[`option${groupOptionIndex + 1}`] ?? variant?.option1;
      const group = normalizeMediaGroup(groupValue);

      if (group) {
        groupValues.add(group);
      }
    }

    return Array.from(groupValues);
  }

  /**
   * @param {Set<string>} mediaIds
   * @param {string} selectedGroup
   * @param {string[]} variantGroupValues
   * @param {HTMLElement[]} mediaElements
   * @returns {Set<string>}
   */
  #addExplicitSelectedGroupMedia(mediaIds, selectedGroup, variantGroupValues, mediaElements) {
    if (!variantGroupValues.length) return mediaIds;

    const nextMediaIds = new Set(mediaIds);

    for (const element of mediaElements) {
      const mediaId = element.dataset.mediaId;
      if (!mediaId) continue;

      const matchedGroups = this.#getElementMatchedVariantGroups(element, variantGroupValues);
      if (matchedGroups.length === 1 && matchedGroups[0] === selectedGroup) {
        nextMediaIds.add(mediaId);
      }
    }

    return nextMediaIds;
  }

  /**
   * @param {Set<string>} mediaIds
   * @param {string} selectedGroup
   * @param {string[]} variantGroupValues
   * @param {HTMLElement[]} mediaElements
   * @returns {Set<string>}
   */
  #removeExplicitOtherGroupMedia(mediaIds, selectedGroup, variantGroupValues, mediaElements) {
    if (!variantGroupValues.length) return mediaIds;

    const nextMediaIds = new Set(mediaIds);

    for (const element of mediaElements) {
      const mediaId = element.dataset.mediaId;
      if (!mediaId || !nextMediaIds.has(mediaId)) continue;

      const matchedGroups = this.#getElementMatchedVariantGroups(element, variantGroupValues);
      if (matchedGroups.length === 1 && matchedGroups[0] !== selectedGroup) {
        nextMediaIds.delete(mediaId);
      }
    }

    return nextMediaIds;
  }

  /**
   * Builds color groups from explicit color anchors plus nearby unlabeled runs.
   * This covers products where normal model photos have generic alt text and
   * only the pack images say "White", "Black", or "Gray".
   *
   * @param {string} selectedGroup
   * @param {string[]} variantGroupValues
   * @param {HTMLElement[]} orderedMediaElements
   * @returns {Set<string>}
   */
  #getGroupedRunMediaIds(selectedGroup, variantGroupValues, orderedMediaElements) {
    if (!selectedGroup || !variantGroupValues.length || !orderedMediaElements.length) return new Set();

    const items = orderedMediaElements
      .map((element) => {
        const mediaId = element.dataset.mediaId || '';
        const matchedGroups = this.#getElementMatchedVariantGroups(element, variantGroupValues);

        return {
          mediaId,
          group: matchedGroups.length === 1 ? matchedGroups[0] : '',
        };
      })
      .filter((item) => item.mediaId);

    if (!items.some((item) => item.group === selectedGroup)) return new Set();

    const mediaIds = new Set();

    for (const item of items) {
      if (item.group === selectedGroup) {
        mediaIds.add(item.mediaId);
      }
    }

    for (let index = 0; index < items.length; index += 1) {
      if (items[index].group) continue;

      const startIndex = index;
      while (index < items.length && !items[index].group) {
        index += 1;
      }

      const endIndex = index;
      const previousGroup = this.#findPreviousMediaGroup(items, startIndex);
      const nextGroup = this.#findNextMediaGroup(items, endIndex);
      const assignedGroups = this.#getAssignedGroupsForRun(previousGroup, nextGroup, endIndex - startIndex);

      if (assignedGroups.has(selectedGroup)) {
        for (let runIndex = startIndex; runIndex < endIndex; runIndex += 1) {
          mediaIds.add(items[runIndex].mediaId);
        }
      }

      index -= 1;
    }

    return mediaIds;
  }

  /**
   * @param {Set<string>} groupedRunMediaIds
   * @param {Set<string>} packSpecificMediaIds
   * @returns {boolean}
   */
  #shouldUseGroupedRunMediaIds(groupedRunMediaIds, packSpecificMediaIds) {
    if (!groupedRunMediaIds.size) return false;

    return Array.from(groupedRunMediaIds).some((mediaId) => !packSpecificMediaIds.has(mediaId));
  }

  /**
   * @param {{ group: string }[]} items
   * @param {number} startIndex
   * @returns {string}
   */
  #findPreviousMediaGroup(items, startIndex) {
    for (let index = startIndex - 1; index >= 0; index -= 1) {
      if (items[index].group) return items[index].group;
    }

    return '';
  }

  /**
   * @param {{ group: string }[]} items
   * @param {number} startIndex
   * @returns {string}
   */
  #findNextMediaGroup(items, startIndex) {
    for (let index = startIndex; index < items.length; index += 1) {
      if (items[index].group) return items[index].group;
    }

    return '';
  }

  /**
   * @param {string} previousGroup
   * @param {string} nextGroup
   * @param {number} runLength
   * @returns {Set<string>}
   */
  #getAssignedGroupsForRun(previousGroup, nextGroup, runLength) {
    if (!previousGroup && !nextGroup) return new Set();
    if (!previousGroup) return new Set([nextGroup]);
    if (!nextGroup) return new Set([previousGroup]);
    if (previousGroup === nextGroup) return new Set([previousGroup]);

    if (runLength <= 1) {
      return new Set([previousGroup, nextGroup]);
    }

    return new Set([nextGroup]);
  }

  /**
   * @param {HTMLElement} element
   * @param {string[]} variantGroupValues
   * @returns {string[]}
   */
  #getElementMatchedVariantGroups(element, variantGroupValues) {
    const groups = new Set();
    const sourceGroup = normalizeMediaGroup(
      [
        element.dataset.mediaGroup,
        element.dataset.mediaGroupLabel,
        element.dataset.mediaGroupSource,
        element.getAttribute('aria-label'),
      ]
        .filter(Boolean)
        .join(' ')
    );

    if (!sourceGroup) return [];

    for (const group of variantGroupValues) {
      if (this.#groupMatchesSource(group, sourceGroup)) {
        groups.add(group);
      }
    }

    return Array.from(groups);
  }

  /**
   * @param {string} group
   * @param {string} sourceGroup
   * @returns {boolean}
   */
  #groupMatchesSource(group, sourceGroup) {
    if (!group || !sourceGroup) return false;
    if (sourceGroup === group || sourceGroup.includes(group)) return true;

    const sourceTokens = sourceGroup.split('-').filter(Boolean);
    const groupTokens = group.split('-').filter((token) => token.length > 2);

    return groupTokens.some((token) => sourceTokens.includes(token));
  }

  /**
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  #hasMediaGroupData(element) {
    return Boolean(this.#getElementMediaGroups(element).length || normalizeMediaGroup(element.dataset.mediaGroupSource));
  }

  /**
   * @param {HTMLElement[]} mediaElements
   * @returns {Set<string>}
   */
  #getPackSpecificMediaIds(mediaElements) {
    return new Set(
      mediaElements
        .filter((element) => {
          const label = [
            element.dataset.mediaGroupSource,
            element.dataset.mediaGroupLabel,
            element.getAttribute('aria-label'),
          ]
            .filter(Boolean)
            .join(' ');

          return /\b\d+\s*-?\s*pack\b/i.test(label) || /\bpack\b/i.test(label);
        })
        .map((element) => element.dataset.mediaId)
        .filter(Boolean)
    );
  }

  /**
   * @param {HTMLElement} element
   * @param {string} selectedGroup
   * @returns {boolean}
   */
  #elementMatchesSelectedGroup(element, selectedGroup) {
    const explicitGroups = this.#getElementMediaGroups(element);
    if (explicitGroups.includes(selectedGroup)) return true;

    const sourceGroup = normalizeMediaGroup(element.dataset.mediaGroupSource);
    if (!sourceGroup) return false;
    return this.#groupMatchesSource(selectedGroup, sourceGroup);
  }

  /**
   * @param {HTMLElement} element
   * @param {boolean} hidden
   */
  #setGroupedMediaHidden(element, hidden) {
    if (element.hidden !== hidden) {
      element.hidden = hidden;
    }

    if (element.getAttribute('aria-hidden') !== String(hidden)) {
      element.toggleAttribute('aria-hidden', hidden);
    }

    if (element instanceof HTMLButtonElement) {
      if (element.disabled !== hidden) {
        element.disabled = hidden;
      }

      const tabIndex = hidden ? -1 : 0;
      if (element.tabIndex !== tabIndex) {
        element.tabIndex = tabIndex;
      }
    }

    const dotItem = element.closest('.slideshow-controls__dots li');
    if (dotItem instanceof HTMLElement) {
      if (dotItem.hidden !== hidden) {
        dotItem.hidden = hidden;
      }

      if (dotItem.getAttribute('aria-hidden') !== String(hidden)) {
        dotItem.toggleAttribute('aria-hidden', hidden);
      }
    }
  }

  /**
   * @param {number} [visibleCount]
   */
  #updateVisibleMediaCount(visibleCount) {
    for (const totalElement of this.querySelectorAll('[data-slideshow-total]')) {
      if (!(totalElement instanceof HTMLElement)) continue;

      totalElement.dataset.originalTotal ||= totalElement.textContent?.trim() || '';
      const total = String(visibleCount ?? totalElement.dataset.originalTotal);
      if (totalElement.textContent !== total) {
        totalElement.textContent = total;
      }
    }
  }

  /**
   * @param {Set<string>} matchingMediaIds
   * @param {string} preferredMediaId
   */
  #reorderVisibleMedia(matchingMediaIds, preferredMediaId) {
    this.#restoreOriginalMediaOrder();

    const orderedMediaIds = this.#getPromotedMediaOrder(matchingMediaIds, preferredMediaId);
    if (!orderedMediaIds.length) return;

    this.#reorderChildrenByMediaId('slideshow-slides', 'slideshow-slide[data-media-id]', orderedMediaIds);
    this.#reorderChildrenByMediaId('.slideshow-controls__thumbnails', '[data-media-id]', orderedMediaIds);
    this.#reorderChildrenByMediaId('.slideshow-controls__dots', 'li', orderedMediaIds);
    this.#reorderChildrenByMediaId('.dialog-thumbnails-list', '[data-media-id]', orderedMediaIds);
    this.#reorderChildrenByMediaId('.dialog-zoomed-gallery', '[data-media-id]', orderedMediaIds);
    this.#reorderChildrenByMediaId('.media-gallery__grid', '[data-media-id]', orderedMediaIds);

    this.slideshow?.updatedCallback?.();
    this.refs.zoomDialogComponent?.updatedCallback?.();
  }

  #restoreOriginalMediaOrder() {
    for (const containerSelector of [
      'slideshow-slides',
      '.slideshow-controls__thumbnails',
      '.slideshow-controls__dots',
      '.dialog-thumbnails-list',
      '.dialog-zoomed-gallery',
      '.media-gallery__grid',
    ]) {
      const container = this.querySelector(containerSelector);
      if (!(container instanceof HTMLElement)) continue;

      const children = Array.from(container.children).filter(
        (child) => child instanceof HTMLElement && child.dataset.originalMediaOrder
      );

      children
        .sort((a, b) => Number(a.dataset.originalMediaOrder) - Number(b.dataset.originalMediaOrder))
        .forEach((child) => container.append(child));
    }

    this.slideshow?.updatedCallback?.();
    this.refs.zoomDialogComponent?.updatedCallback?.();
  }

  /**
   * @param {Set<string>} matchingMediaIds
   * @param {string} preferredMediaId
   * @returns {string[]}
   */
  #getPromotedMediaOrder(matchingMediaIds, preferredMediaId) {
    const visibleMediaIds = this.#getOrderedMediaElements()
      .map((element) => element.dataset.mediaId)
      .filter((mediaId) => mediaId && matchingMediaIds.has(mediaId));

    if (!preferredMediaId || !matchingMediaIds.has(preferredMediaId)) {
      return visibleMediaIds;
    }

    return [preferredMediaId, ...visibleMediaIds.filter((mediaId) => mediaId !== preferredMediaId)];
  }

  /**
   * @param {string} containerSelector
   * @param {string} itemSelector
   * @param {string[]} orderedMediaIds
   */
  #reorderChildrenByMediaId(containerSelector, itemSelector, orderedMediaIds) {
    const container = this.querySelector(containerSelector);
    if (!(container instanceof HTMLElement)) return;

    const items = Array.from(container.querySelectorAll(itemSelector)).filter(
      (item) => item instanceof HTMLElement
    );
    if (!items.length) return;

    items.forEach((item, index) => {
      item.dataset.originalMediaOrder ||= String(index);
    });

    for (const mediaId of orderedMediaIds) {
      const item = items.find((candidate) => {
        if (!(candidate instanceof HTMLElement)) return false;

        const mediaElement = candidate.matches('[data-media-id]')
          ? candidate
          : candidate.querySelector('[data-media-id]');

        return mediaElement instanceof HTMLElement && mediaElement.dataset.mediaId === mediaId;
      });

      if (item) {
        container.append(item);
      }
    }
  }

  async #selectFirstVisibleMedia() {
    const slides = Array.from(this.querySelectorAll('slideshow-slide[data-media-id]'));
    const firstVisibleSlide = slides.find((slide) => slide instanceof HTMLElement && !slide.hidden);
    if (!(firstVisibleSlide instanceof HTMLElement)) return;

    if (firstVisibleSlide.dataset.mediaId) {
      await this.#selectVisibleMedia(firstVisibleSlide.dataset.mediaId);
    }
  }

  /**
   * @param {string} mediaId
   */
  async #selectVisibleMedia(mediaId) {
    const slide = Array.from(this.querySelectorAll('slideshow-slide[data-media-id]')).find(
      (element) => element instanceof HTMLElement && element.dataset.mediaId === mediaId
    );
    if (!(slide instanceof HTMLElement) || slide.hidden) return;

    const slideId = slide.getAttribute('slide-id');

    if (this.slideshow) {
      if (slideId) {
        await this.slideshow.select({ id: slideId }, undefined, { animate: false });
      } else {
        await this.slideshow.select(0, undefined, { animate: false });
      }
    }

    this.#selectMediaControls(mediaId);
  }

  /**
   * @param {Set<string>} matchingMediaIds
   * @returns {{
   *   hasPackSelection: boolean,
   *   preferredMediaId: string,
   *   selectedPackMediaIds: Set<string>,
   *   otherPackMediaIds: Set<string>
   * }}
   */
  #getSelectedPackMediaFilter(matchingMediaIds, galleryMediaIds, packSpecificMediaIds) {
    const emptyFilter = {
      hasPackSelection: false,
      preferredMediaId: '',
      selectedPackMediaIds: new Set(),
      otherPackMediaIds: new Set(),
    };
    const packOptionIndex = Number.parseInt(this.dataset.packOptionIndex || '-1', 10);
    if (packOptionIndex < 0) return emptyFilter;

    const selectedPack = normalizeMediaGroup(this.#getSelectedOptionValueByIndex(packOptionIndex));
    if (!selectedPack) return emptyFilter;

    const variants = this.#getProductVariants();
    if (!Array.isArray(variants)) return emptyFilter;

    const groupOptionIndex = Number.parseInt(this.dataset.groupOptionIndex || '0', 10) || 0;
    const selectedGroup = this.#getSelectedMediaGroup();
    const selectedPackMediaIds = new Set();
    const otherPackMediaIds = new Set();

    for (const candidate of variants) {
      const candidateGroup = normalizeMediaGroup(
        Array.isArray(candidate?.options)
          ? candidate.options[groupOptionIndex] ?? candidate.options[0]
          : candidate?.[`option${groupOptionIndex + 1}`] ?? candidate?.option1
      );
      const candidatePack = normalizeMediaGroup(
        Array.isArray(candidate?.options)
          ? candidate.options[packOptionIndex]
          : candidate?.[`option${packOptionIndex + 1}`]
      );
      const mediaId = String(candidate?.featured_media?.id ?? candidate?.featured_image?.id ?? '');

      if (!mediaId || candidateGroup !== selectedGroup || !candidatePack || !galleryMediaIds.has(mediaId)) {
        continue;
      }

      if (candidatePack === selectedPack) {
        selectedPackMediaIds.add(mediaId);
      } else if (packSpecificMediaIds.has(mediaId)) {
        otherPackMediaIds.add(mediaId);
      }
    }

    for (const mediaId of selectedPackMediaIds) {
      otherPackMediaIds.delete(mediaId);
    }

    const preferredMediaId = this.#getPreferredPackMediaId(
      selectedPackMediaIds,
      matchingMediaIds,
      packSpecificMediaIds
    );

    return {
      hasPackSelection: true,
      preferredMediaId,
      selectedPackMediaIds,
      otherPackMediaIds,
    };
  }

  /**
   * @param {Set<string>} selectedPackMediaIds
   * @param {Set<string>} matchingMediaIds
   * @param {Set<string>} packSpecificMediaIds
   * @returns {string}
   */
  #getPreferredPackMediaId(selectedPackMediaIds, matchingMediaIds, packSpecificMediaIds) {
    if (!selectedPackMediaIds.size) return '';

    const currentVariant = this.#getCurrentVariant();
    const currentMediaId = String(currentVariant?.featured_media?.id ?? currentVariant?.featured_image?.id ?? '');

    if (currentMediaId && selectedPackMediaIds.has(currentMediaId) && packSpecificMediaIds.has(currentMediaId)) {
      return currentMediaId;
    }

    const mediaOrder = this.#getOrderedMediaElements()
      .map((element) => element.dataset.mediaId)
      .filter(Boolean);

    return (
      mediaOrder.find((mediaId) => selectedPackMediaIds.has(mediaId) && packSpecificMediaIds.has(mediaId)) ||
      (currentMediaId && selectedPackMediaIds.has(currentMediaId) ? currentMediaId : '') ||
      mediaOrder.find((mediaId) => selectedPackMediaIds.has(mediaId) && matchingMediaIds.has(mediaId)) ||
      mediaOrder.find((mediaId) => selectedPackMediaIds.has(mediaId)) ||
      ''
    );
  }

  /**
   * @param {string} mediaId
   */
  #selectMediaControls(mediaId) {
    const controls = this.querySelectorAll(
      [
        'slideshow-controls [data-media-id]',
        '.dialog-thumbnails-list__thumbnail[data-media-id]',
      ].join(',')
    );

    for (const control of controls) {
      if (!(control instanceof HTMLElement) || control.hidden) continue;
      const selected = `${control.dataset.mediaId === mediaId}`;
      if (control.getAttribute('aria-selected') !== selected) {
        control.setAttribute('aria-selected', selected);
      }
    }
  }

  #getSelectedMediaGroup() {
    const optionIndex = Number.parseInt(this.dataset.groupOptionIndex || '0', 10) || 0;

    return normalizeMediaGroup(this.#getSelectedOptionValueByIndex(optionIndex));
  }

  /**
   * Reads the live selected option first, then falls back to the current
   * variant JSON. This keeps Pack clicks from being overwritten by the color
   * group's default featured image during Horizon's async gallery refresh.
   *
   * @param {number} optionIndex
   * @returns {string}
   */
  #getSelectedOptionValueByIndex(optionIndex) {
    const section = this.closest('.shopify-section');
    const optionPosition = optionIndex + 1;
    const checkedInput = section?.querySelector(
      `variant-picker input:checked[data-input-id^="${optionPosition}-"], swatches-variant-picker-component input:checked[data-input-id^="${optionPosition}-"]`
    );

    if (checkedInput instanceof HTMLInputElement && checkedInput.value) {
      return checkedInput.value;
    }

    const selectedOption = section?.querySelector(
      `variant-picker select option:checked[data-input-id^="${optionPosition}-"], swatches-variant-picker-component select option:checked[data-input-id^="${optionPosition}-"]`
    );

    if (selectedOption instanceof HTMLOptionElement && selectedOption.value) {
      return selectedOption.value;
    }

    const variant = this.#getCurrentVariant();
    if (!variant) return '';

    return Array.isArray(variant.options)
      ? variant.options[optionIndex] ?? variant.options[0] ?? ''
      : variant[`option${optionPosition}`] ?? variant.option1 ?? '';
  }

  #getCurrentVariant() {
    const section = this.closest('.shopify-section');
    const variantId = this.#getCurrentVariantId(section);
    const variants = this.#getProductVariants();

    if (variantId && Array.isArray(variants)) {
      const variant = variants.find((item) => String(item?.id) === String(variantId));
      if (variant) return variant;
    }

    const selectedVariant = parseJsonScript(this.querySelector('[data-selected-variant]'));
    if (selectedVariant) return selectedVariant;

    return null;
  }

  /**
   * @returns {any[] | null}
   */
  #getProductVariants() {
    if (this.#productVariantsCache !== undefined) {
      return this.#productVariantsCache;
    }

    const variants = parseJsonScript(this.querySelector('[data-product-variants]'));
    this.#productVariantsCache = Array.isArray(variants) ? variants : null;

    return this.#productVariantsCache;
  }

  /**
   * @param {Element | null} section
   * @returns {string}
   */
  #getCurrentVariantId(section) {
    const formVariantInput = section?.querySelector(
      'product-form-component [ref="variantId"], product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]'
    );

    if (formVariantInput instanceof HTMLInputElement && formVariantInput.value) {
      return formVariantInput.value;
    }

    return new URL(window.location.href).searchParams.get('variant') || '';
  }

  // ──────────────────────────────────────────────
  //  External Variant Bridge (Globo compatibility)
  // ──────────────────────────────────────────────

  /**
   * Sets up multi-layered detection of external variant changes.
   *
   * Detection layers:
   * 1. Document `change` event (primary) — real-world pattern
   * 2. URL monitoring via monkey-patched History API
   * 3. MutationObserver on hidden input value attribute
   *
   * @param {AbortSignal} signal - Lifecycle signal from the component controller.
   */
  #setupExternalVariantBridge(signal) {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const productForm = section.querySelector('product-form-component');
    const variantInput = productForm?.querySelector('[ref="variantId"]');

    // Track the last known variant ID
    this.#lastProcessedExternalVariantId = variantInput?.value || '';

    // ── Layer 1: Document-level change event (real-world Fix A/C) ──
    // This catches change events from ANY source and checks whether
    // the hidden variant input's value has changed to a new variant
    // that wasn't already handled by the native event chain.
    document.addEventListener(
      'change',
      (event) => {
        if (signal.aborted) return;

        // Skip if the change originated from the native variant-picker's own
        // inputs (radios/selects). The native flow already handles those.
        if (event.target?.closest?.('variant-picker, swatches-variant-picker-component')) {
          return;
        }

        this.#detectAndBridge(section);
      },
      { signal }
    );

    // ── Layer 2: URL monitoring (History API) ──
    window.addEventListener(
      'history:url-changed',
      () => {
        if (signal.aborted) return;
        this.#detectAndBridge(section);
      },
      { signal }
    );

    // ── Layer 3: MutationObserver on input value attribute ──
    if (variantInput) {
      const observer = new MutationObserver(() => {
        if (signal.aborted) {
          observer.disconnect();
          return;
        }
        this.#detectAndBridge(section);
      });
      observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
      signal.addEventListener(
        'abort',
        () => {
          observer.disconnect();
        },
        { once: true }
      );
    }
  }

  /**
   * Checks whether the hidden variant input's value has changed to an
   * externally-selected variant and, if so, triggers the bridge.
   *
   * @param {Element} section - The closest `.shopify-section` element.
   */
  #detectAndBridge(section) {
    if (this.#controller.signal.aborted) return;

    const productForm = section.querySelector('product-form-component');
    const variantInput = productForm?.querySelector('[ref="variantId"]');
    if (!variantInput) return;

    const newValue = variantInput.value;
    // Guard: no value, unchanged value, or empty string
    if (!newValue || newValue === this.#lastProcessedExternalVariantId) return;

    // Guard: this variant was already handled by a native VariantUpdateEvent
    if (recentlyNotifiedVariants.has(newValue)) return;

    this.#lastProcessedExternalVariantId = newValue;

    this.#handleExternalVariantChange(newValue);
  }

  /**
   * Fetches server-rendered HTML for the externally-selected variant and
   * dispatches a native `VariantUpdateEvent` so the entire theme reacts.
   *
   * After the event is dispatched, also fires a `resize` event (real-world
   * Fix C pattern) to force slideshow/gallery layout recalculation.
   *
   * @param {string} variantId - The newly selected variant ID.
   */
  async #handleExternalVariantChange(variantId) {
    // Cancel any in-flight request from a previous rapid variant change
    this.#externalFetchAbortController?.abort();
    this.#externalFetchAbortController = new AbortController();
    const { signal } = this.#externalFetchAbortController;

    if (this.#controller.signal.aborted) return;

    // Determine the product URL
    const section = this.closest('.shopify-section');
    if (!section) return;

    const productForm = section.querySelector('product-form-component');
    const picker = section.querySelector('variant-picker, swatches-variant-picker-component');
    let productUrl =
      productForm?.dataset.productUrl || picker?.dataset.productUrl || window.location.pathname;

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);

    try {
      const response = await fetch(url.toString(), { signal });
      if (!response.ok) return;

      const htmlText = await response.text();
      const doc = new DOMParser().parseFromString(htmlText, 'text/html');

      // Extract variant data from the server-rendered variant-picker
      const responsePicker = doc.querySelector('variant-picker, swatches-variant-picker-component');
      const jsonScript = responsePicker?.querySelector('script[type="application/json"]');
      /** @type {{id?: string|number, available?: boolean, featured_media?: {preview_image?: {src?: string}}, sku?: string}} */
      const variantData = jsonScript?.textContent
        ? JSON.parse(jsonScript.textContent)
        : { id: variantId };

      // Get the product ID from the response (for productId guards)
      const responseProductForm = doc.querySelector('product-form-component');
      const productId =
        responseProductForm?.dataset.productId ||
        responsePicker?.dataset.productId ||
        productForm?.dataset.productId ||
        '';

      // Mark as notified BEFORE dispatching so our detection layers
      // (change listener, URL handler) skip this variant.
      markVariantNotified(variantId);

      // Dispatch the same VariantUpdateEvent that the native variant-picker emits.
      // All theme components listen for this and update reactively.
      const event = new VariantUpdateEvent(variantData, 'external', {
        html: doc,
        productId,
      });

      // Dispatch on the product-form so event.target.dataset.productId is
      // available for product-price, product-inventory, and product-sku guards.
      const dispatchTarget = productForm || section;
      dispatchTarget.dispatchEvent(event);

      // Real-world Fix C: dispatch resize event to force slideshow/gallery
      // layout recalculation. Some themes' slideshow components rely on
      // window resize handlers to recalculate slide dimensions when the
      // media changes (aspect ratio, number of items, etc.).
      window.dispatchEvent(new Event('resize'));
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (error.name === 'TypeError' && error.message?.includes('fetch')) return;
      console.warn('Variant media bridge:', error);
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}